import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/types";
import { z } from "zod";

export type FeeScheduleTemplateAccessJSON = z.infer<
  typeof FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ
>;

export type FeeScheduleTemplateAccessParams = z.infer<
  typeof FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ
>;

export default class FeeScheduleTemplateAccessSchema {
  public static readonly FeeScheduleTemplateAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.FeeScheduleTemplateAccess),
    templateId: z.uuid().nullish(),
    accessLevel: z.enum(AccessLevel).nullish(),
  });

  public static readonly FeeScheduleTemplateAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.FeeScheduleTemplateAccess),
    templateId: z.instanceof(Guid).optional(),
    accessLevel: z.enum(AccessLevel).optional(),
  });

  public static validate(values: unknown): FeeScheduleTemplateAccessParams {
    return parseOrThrow(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): FeeScheduleTemplateAccessJSON {
    return parseOrThrow(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleTemplateAccessParams {
    try {
      FeeScheduleTemplateAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is FeeScheduleTemplateAccessJSON {
    try {
      FeeScheduleTemplateAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: FeeScheduleTemplateAccessJSON): FeeScheduleTemplateAccessParams {
    const parsed = parseOrThrow(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ, data, {
      logError: true,
      schemaName: "FeeScheduleTemplateAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return FeeScheduleTemplateAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: FeeScheduleTemplateAccessParams): FeeScheduleTemplateAccessJSON {
    const parsed = FeeScheduleTemplateAccessSchema.validate(notification);
    return FeeScheduleTemplateAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ,
    FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.FeeScheduleTemplateAccess as const, 
        templateId: Guid.fromJSON(data.templateId) ?? undefined,
        accessLevel: data.accessLevel ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.FeeScheduleTemplateAccess as const,
        templateId: resource.templateId?.toJSON(),
        accessLevel: resource.accessLevel,
      }),
    }
  );
}
